import { useNavigate } from 'react-router-dom';
import { UsersPlusIcon } from '../../../../assets/icons/UsersPlusIcon';
import { CircleInfoIcon } from '../../../../assets/icons/CircleInfo';
import Button from '../../../widgets/Button';
import { Typography } from '../../../widgets/Typography';
import TooltipV2 from '../../../widgets/ToolTipV2';
import { statusColors, transactionLabels } from '../constants';
import TransactionsTable from './TransactionsTable';
import useSyncFilesList from '../hooks/useSyncFilesList';
import Pagination from '../../../widgets/Pagination';
import React from 'react';
import { formatDateToDisplayLabel } from '../../../../utilities/common/date';
import { RemarksCell } from './RemarksCell';

export default function ActiveMembersSyncView() {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = React.useState(1);

  const { data: { ingestions, pagination, canUpload } = {} } =
    useSyncFilesList(currentPage);

  return (
    <div className="bg-gold-200 flex flex-col">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col">
          <Typography variant="body-md" weight="semibold" className="text-left">
            Sync Active Members
          </Typography>
          <Typography
            variant="body-sm"
            weight="regular"
            className="text-grey-700 text-left"
          >
            Upload your latest employee list to sync memberships. New entries
            are onboarded, removed entries are offboarded.
          </Typography>
        </div>
        {!canUpload ? (
          <TooltipV2
            wrapperClassName="border text-gray-800"
            wrapperBackgroundColor="var(--color-amber-25, #FFFCF5)"
            wrapperBorderColor="var(--color-amber-100, #FEF0C7)"
            content={
              <div className="flex max-w-xs flex-row items-start gap-2">
                <CircleInfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <Typography variant="body-sm" className="text-grey-800">
                  A previous upload is currently under review. You can upload
                  again once it is approved or rejected.
                </Typography>
              </div>
            }
          >
            <Button
              leftIcon={<UsersPlusIcon className="stroke-neutral-500" />}
              variant="primary"
              disabled
            >
              Upload Member List
            </Button>
          </TooltipV2>
        ) : (
          <Button
            leftIcon={<UsersPlusIcon className="stroke-white" />}
            variant="primary"
            onClick={() => navigate('/members/sync')}
          >
            Upload Member List
          </Button>
        )}
      </div>
      <div className="mt-6">
        <TransactionsTable>
          <TransactionsTable.Header>
            <TransactionsTable.HeaderCell className="w-[7%]">
              ID
            </TransactionsTable.HeaderCell>
            <TransactionsTable.HeaderCell className="w-[20%]">
              File Name
            </TransactionsTable.HeaderCell>
            <TransactionsTable.HeaderCell className="w-[13%]">
              Upload Date
            </TransactionsTable.HeaderCell>
            <TransactionsTable.HeaderCell className="w-[10%]">
              Total records
            </TransactionsTable.HeaderCell>
            <TransactionsTable.HeaderCell className="w-[13%]">
              Status
            </TransactionsTable.HeaderCell>
            <TransactionsTable.HeaderCell className="w-[12%]">
              Uploaded By
            </TransactionsTable.HeaderCell>
            <TransactionsTable.HeaderCell className="w-[25%]">
              Remarks
            </TransactionsTable.HeaderCell>
          </TransactionsTable.Header>
          <TransactionsTable.Body>
            {ingestions?.length ? (
              ingestions.map((item) => (
                <TransactionsTable.Row key={item.id}>
                  <TransactionsTable.Cell>{item.id}</TransactionsTable.Cell>
                  <TransactionsTable.Cell>
                    <div className="max-w-47.5 truncate" title={item.fileName}>
                      {item.fileName}
                    </div>
                  </TransactionsTable.Cell>
                  <TransactionsTable.Cell>
                    {formatDateToDisplayLabel(item.createdAt)}
                  </TransactionsTable.Cell>
                  <TransactionsTable.Cell className="flex flex-row">
                    {item.rowCount}
                  </TransactionsTable.Cell>
                  <TransactionsTable.Cell>
                    <div
                      className={`w-fit rounded-sm px-2 py-1 text-xs ${statusColors[item.status]}`}
                    >
                      {transactionLabels[item.status]}
                    </div>
                  </TransactionsTable.Cell>
                  <TransactionsTable.Cell>
                    {item.uploadedBy || '-'}
                  </TransactionsTable.Cell>
                  <TransactionsTable.Cell>
                    {item.comments ? (
                      <RemarksCell comments={item.comments} />
                    ) : (
                      '-'
                    )}
                  </TransactionsTable.Cell>
                </TransactionsTable.Row>
              ))
            ) : (
              <TransactionsTable.Row>
                <TransactionsTable.Cell colSpan={8}>
                  <div className="flex flex-col items-center justify-center py-10">
                    <Typography
                      variant="body-md"
                      weight="semibold"
                      className="text-taupe-800"
                    >
                      No uploads yet
                    </Typography>
                    <Typography
                      variant="body-sm"
                      weight="regular"
                      className="text-grey-700 mt-1"
                    >
                      Upload a member list to get started with syncing
                      memberships.
                    </Typography>
                  </div>
                </TransactionsTable.Cell>
              </TransactionsTable.Row>
            )}
          </TransactionsTable.Body>
        </TransactionsTable>
        <Pagination
          totalItems={pagination?.total || 0}
          itemsPerPage={20}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
